import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MirageTheme } from "@mirage/theme/MuiMirage.module";
import { Badge, Box, Tooltip, Typography } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import React from "react";
import { ModeTypes } from "../mirageGlobalButtons/MirageRecordActionButtons";
import "./Field.css";
import { ContentType } from "@shared/mirage/metadata/Contracts";

interface MirageFormControlProps {
    label: string;
    children: JSX.Element;
    mode: ModeTypes;
    error?: boolean;
    helpText?: (() => JSX.Element) | string | null;
    className?: string;
    labelClassName?: string;
    errorMsgClassName?: string;
    readOnly?: boolean;
    isRequired?: boolean;
    errorMsg?: string;
    beforeLabelAdornment?: JSX.Element;
    endWatermark?: string;
    contentType?: ContentType;
}

const MirageFormControl: React.FC<MirageFormControlProps> = ({
    label,
    children,
    mode,
    readOnly,
    helpText,
    className,
    labelClassName,
    errorMsgClassName,
    error,
    errorMsg,
    isRequired,
    beforeLabelAdornment,
    endWatermark,
    contentType,
}) => {
    const containerClassName = mode === "edit" ? "mirageFormControl" : "mirageFormControl mirageFormControl--view";
    return (
        <FormControl className={`${containerClassName} ${className ? className : ""}`.trim()} focused={false} unselectable={readOnly ? "on" : "off"} error={error}>
            <Box className={getInputContainerClassName()}>
                <CustomLabel className={labelClassName} label={label} mode={mode} helpText={helpText} isRequired={isRequired} beforeLabelAdornment={beforeLabelAdornment} />
                {children}
                {endWatermark ? (
                    <Box className="mirageFormControl__endWatermark" sx={{ color: MirageTheme.palette.grey["800"] }}>
                        <Typography fontSize={"13px"}>{endWatermark}</Typography>
                    </Box>
                ) : null}
            </Box>
            {errorMsg && (
                <Box className={(errorMsgClassName || "") + " mirageFormControl__errorMsg"} sx={{ color: MirageTheme.palette.error.main }}>
                    <Typography fontSize={"13px"}>{errorMsg}</Typography>
                </Box>
            )}
        </FormControl>
    );
    function getInputContainerClassName() {
        if (mode === "view") {
            if (contentType === "CHECKBOX") {
                return "mirageFormControl__inputContainer mirageFormControl__inputContainer--alignCenter";
            }
            return "mirageFormControl__inputContainer";
        }
        if (mode === "edit") {
            if (contentType === "TEXTAREA") {
                return "mirageFormControl__inputContainer mirageFormControl__inputContainer--alignBaseline";
            }
            return "mirageFormControl__inputContainer mirageFormControl__inputContainer--alignCenter";
        }
    }
};

const CustomLabel: React.FC<CustomLabelProps> = ({ label, mode, helpText, isRequired, className, beforeLabelAdornment }) => {
    const infoColor = MirageTheme.palette.info.main;
    const required = mode === "edit" && isRequired;
    const noLabelClass = !label ? "mirageFormControl__label--autoWidth" : "";

    if (mode === "edit" && helpText) {
        const infoIcon = getInfoIcon(helpText);
        return (
            <Badge badgeContent={infoIcon}>
                <FormLabel required={required} className={`mirageFormControl__label ${noLabelClass} ${className ? className : ""}`.trim()}>
                    {label}
                </FormLabel>
            </Badge>
        );
    }

    return (
        <FormLabel required={required} className={`mirageFormControl__label ${noLabelClass} ${className ? className : ""}`.trim()}>
            {beforeLabelAdornment}
            {label}
        </FormLabel>
    );
    function getInfoIcon(helpText: (() => JSX.Element) | string | null) {
        if (typeof helpText === "string") {
            return (
                <Tooltip title={helpText} placement="top">
                    <FontAwesomeIcon className="mirageFormControl__infoIcon" icon={MirageTheme.icons.infoIcon} style={MirageTheme.icons.sizeS} color={infoColor} />
                </Tooltip>
            );
        }
        if (typeof helpText === "function") {
            const helpTextElement = helpText();
            return helpTextElement;
        }
        return null;
    }
};

interface CustomLabelProps {
    label: string;
    mode: ModeTypes;
    helpText?: (() => JSX.Element) | string | null;
    isRequired?: boolean;
    className?: string;
    beforeLabelAdornment?: JSX.Element;
}

export default MirageFormControl;
